import React, { useState, useId } from 'react';

type InputOption = {
  value: string;
  label: string;
};

interface InputFieldProps {
  type: string;
  name: string;
  id?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
  pattern?: string;
  options?: InputOption[];
  isOverlay?: boolean;
  zIndex?: number;
  className?: string;
  eventHandlers?: {
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  };
}

export const InputField = ({
  type = 'text',
  name,
  id,
  label = '',
  placeholder = '',
  defaultValue = '',
  required = false,
  pattern = '',
  options = [],
  isOverlay = false,
  zIndex = 10,
  className = '',
  eventHandlers = {},
}: InputFieldProps) => {
  const [value, setValue] = useState(defaultValue);
  const uniqueId = useId().replace(/:/g, '');
  const inputId = id || `${name}-${uniqueId}`;
  
  // Common classes for styling inputs
  const baseInputClasses = 'w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent';
  const labelClasses = 'block mb-2 text-sm font-medium';
  const errorClasses = 'mt-1 text-red-600 text-sm';
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValue(e.target.value);
    
    // Call the onChange handler from props if it exists
    if (eventHandlers.onChange) {
      eventHandlers.onChange(e);
    }
  };
  
  const renderInput = () => {
    switch (type) {
      case 'textarea':
        return (
          <textarea
            id={inputId}
            name={name}
            placeholder={placeholder}
            defaultValue={defaultValue}
            required={required}
            className={`${baseInputClasses} h-32 resize-none`}
            onChange={handleChange}
            onBlur={eventHandlers.onBlur}
            onFocus={eventHandlers.onFocus}
            onKeyPress={eventHandlers.onKeyPress as any}
          />
        );
        
      case 'select':
        return (
          <select
            id={inputId}
            name={name}
            defaultValue={defaultValue}
            required={required}
            className={baseInputClasses}
            onChange={handleChange}
            onBlur={eventHandlers.onBlur}
            onFocus={eventHandlers.onFocus}
          >
            <option value="" disabled>
              {placeholder || 'Select an option'}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        
      case 'checkbox':
        return (
          <div className="space-y-2">
            {options.length > 0 ? (
              options.map((option) => (
                <div key={option.value} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`${inputId}-${option.value}`}
                    name={name}
                    value={option.value}
                    defaultChecked={defaultValue === option.value}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    onChange={eventHandlers.onChange as any}
                    onBlur={eventHandlers.onBlur as any}
                    onFocus={eventHandlers.onFocus as any}
                  />
                  <label
                    htmlFor={`${inputId}-${option.value}`}
                    className="ml-2 text-sm font-medium"
                  >
                    {option.label}
                  </label>
                </div>
              ))
            ) : (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={inputId}
                  name={name}
                  defaultChecked={defaultValue === 'true' || defaultValue === '1'}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  onChange={eventHandlers.onChange as any}
                  onBlur={eventHandlers.onBlur as any}
                  onFocus={eventHandlers.onFocus as any}
                />
                <label
                  htmlFor={inputId}
                  className="ml-2 text-sm font-medium"
                >
                  {label}
                </label>
              </div>
            )}
          </div>
        );
        
      case 'radio':
        return (
          <div className="space-y-2">
            {options.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`${inputId}-${option.value}`}
                  name={name}
                  value={option.value}
                  defaultChecked={defaultValue === option.value}
                  required={required}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  onChange={eventHandlers.onChange as any}
                  onBlur={eventHandlers.onBlur as any}
                  onFocus={eventHandlers.onFocus as any}
                />
                <label
                  htmlFor={`${inputId}-${option.value}`}
                  className="ml-2 text-sm font-medium"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );
        
      default:
        return (
          <input
            type={type}
            id={inputId}
            name={name}
            placeholder={placeholder}
            defaultValue={defaultValue}
            required={required}
            {...(pattern && { pattern })}
            className={baseInputClasses}
            onChange={handleChange}
            onBlur={eventHandlers.onBlur}
            onFocus={eventHandlers.onFocus}
            onKeyPress={eventHandlers.onKeyPress}
          />
        );
    }
  };

  const containerStyles: React.CSSProperties = isOverlay
    ? { position: 'absolute', zIndex }
    : {};

  return (
    <div 
      className={`input-field-container relative ${className}`}
      style={containerStyles}
    >
      {label && type !== 'checkbox' && (
        <label htmlFor={inputId} className={labelClasses}>
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}
      {renderInput()}
    </div>
  );
};

export default InputField;